import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { Link } from 'react-router-dom';
import { Car, Search, MapPin, Calendar, SlidersHorizontal, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';
import BannierePublicitaire from '../components/BannierePublicitaire';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtresOuverts, setFiltresOuverts] = useState(false);
  const [filtres, setFiltres] = useState({
    recherche: '', prix_max: '', localisation: '', transmission: '', type_vehicule: ''
  });

  useEffect(() => {
    chargerLocations(filtres);
  }, []);

  const chargerLocations = async (params = filtres) => {
    setLoading(true);
    try {
      const res = await API.get('/locations', {
        params: {
          recherche: params.recherche || undefined,
          prix_max: params.prix_max || undefined,
          localisation: params.localisation || undefined,
          transmission: params.transmission || undefined,
          type_vehicule: params.type_vehicule || undefined,
        }
      });
      setLocations(res.data.locations);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltres = (e) => {
    e.preventDefault();
    chargerLocations(filtres);
    setFiltresOuverts(false);
  };

  const handleReset = () => {
    const empty = { recherche: '', prix_max: '', localisation: '', transmission: '', type_vehicule: '' };
    setFiltres(empty);
    chargerLocations(empty);
  };

  const nbFiltresActifs = Object.values(filtres).filter(v => v !== '').length;
  const inputClass = "w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 transition";
  const labelClass = "block text-xs font-bold text-gray-500 mb-1";

  const ContenuFiltres = () => (
    <form onSubmit={handleFiltres} className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100 sticky top-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} style={{ color: '#F5A623' }} />
          <h2 className="font-black text-sm text-gray-800">Filtres</h2>
        </div>
        <button type="button" onClick={handleReset} className="text-xs text-gray-400 hover:text-red-400 flex items-center gap-1 transition">
          <X size={12} /> Réinitialiser
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Recherche</label>
          <input type="text" placeholder="Marque, modèle, type..." value={filtres.recherche}
            onChange={(e) => setFiltres({ ...filtres, recherche: e.target.value })} className={inputClass} />
        </div>
        <div style={{ height: '1px', background: '#f3f4f6' }} />
        <div>
          <label className={labelClass}>Prix max / jour (FCFA)</label>
          <input type="number" placeholder="Ex: 50000" value={filtres.prix_max}
            onChange={(e) => setFiltres({ ...filtres, prix_max: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Localisation</label>
          <input type="text" placeholder="Ex: Libreville" value={filtres.localisation}
            onChange={(e) => setFiltres({ ...filtres, localisation: e.target.value })} className={inputClass} />
        </div>
        <div style={{ height: '1px', background: '#f3f4f6' }} />
        <div>
          <label className={labelClass}>Transmission</label>
          <select value={filtres.transmission} onChange={(e) => setFiltres({ ...filtres, transmission: e.target.value })} className={inputClass}>
            <option value="">Toutes</option>
            <option value="manuelle">Manuelle</option>
            <option value="automatique">Automatique</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Type de véhicule</label>
          <select value={filtres.type_vehicule} onChange={(e) => setFiltres({ ...filtres, type_vehicule: e.target.value })} className={inputClass}>
            <option value="">Tous</option>
            <option value="berline">Berline</option>
            <option value="suv">SUV</option>
            <option value="4x4">4x4</option>
            <option value="pickup">Pick-up</option>
            <option value="utilitaire">Utilitaire</option>
            <option value="engin">Engin</option>
            <option value="citadine">Citadine</option>
            <option value="monospace">Monospace</option>
            <option value="moto">Moto</option>
          </select>
        </div>
        <button type="submit" style={{ background: '#F5A623' }}
          className="w-full text-black rounded-xl py-2.5 font-black text-sm flex items-center justify-center gap-2 hover:opacity-90 transition">
          <Search size={15} /> Filtrer
        </button>
      </div>
    </form>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-white text-xl font-black flex items-center gap-2">
              <Calendar size={20} style={{ color: '#F5A623' }} />
              Location de véhicules
            </h1>
            <p style={{ color: '#F5A623' }} className="text-sm mt-1">
              {locations.length} véhicule(s) disponible(s) à la location
            </p>
          </div>
          {/* Bouton Filtres — visible uniquement sur mobile */}
          <button
            onClick={() => setFiltresOuverts(!filtresOuverts)}
            className="md:hidden flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm relative"
            style={{ background: filtresOuverts ? '#F5A623' : 'white', color: filtresOuverts ? 'black' : '#374151' }}
          >
            <SlidersHorizontal size={16} />
            Filtres
            {nbFiltresActifs > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-xs font-black flex items-center justify-center"
                style={{ background: '#ef4444', color: 'white' }}>
                {nbFiltresActifs}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Panneau filtres mobile — rétractable */}
      {filtresOuverts && (
        <div className="md:hidden px-4 pt-4 pb-2">
          {ContenuFiltres()}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">

          {/* Filtres desktop — colonne gauche */}
          <div className="hidden md:block w-64 flex-shrink-0">
            {ContenuFiltres()}
          </div>

          {/* Résultats */}
          <div className="flex-1 min-w-0">
            <BannierePublicitaire emplacement="locations_top" className="h-20 mb-4" />

            {loading ? (
              <div className="text-center py-12 text-gray-400">Chargement...</div>
            ) : locations.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                <Car size={60} className="mx-auto mb-4 text-gray-200" />
                <p className="text-gray-400 font-semibold">Aucun véhicule disponible</p>
                <p className="text-gray-300 text-sm mt-1">Essayez de modifier vos filtres</p>
              </div>
            ) : (
              /* 2 colonnes mobile / 3 colonnes desktop */
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {locations.map(location => (
                  <Link key={location.id} to={`/locations/${location.id}`}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition group border border-gray-100">
                    <div className="h-32 bg-gray-100 overflow-hidden relative">
                      {location.photo_principale ? (
                        <img src={getImageUrl(location.photo_principale)} alt={location.titre}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Car size={40} className="text-gray-300" />
                        </div>
                      )}
                      <div style={{ background: '#F5A623' }}
                        className="absolute top-2 right-2 text-black text-xs font-black px-2 py-0.5 rounded-full">
                        Location
                      </div>
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-gray-900 font-semibold text-xs truncate">{location.titre}</h3>
                      <p className="font-black text-gray-900 text-sm mt-1">
                        {parseInt(location.prix_jour).toLocaleString('fr-FR')}
                        <span className="text-xs font-normal text-gray-400"> FCFA/j</span>
                      </p>
                      {location.localisation && (
                        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1 truncate">
                          <MapPin size={10} /> {location.localisation}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        Par {location.prenom} {location.nom}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Locations;
